/**
 * QuestionnaireStepView — renders a single step with title, description, and questions.
 */
import React from 'react';
import type { QuestionnaireStep, QuestionDefinition } from '@/types/pm-types';
import { QuestionInput } from './QuestionInput';

export interface QuestionnaireStepViewProps {
  step: QuestionnaireStep;
  answers: Record<string, unknown>;
  onAnswerChange: (questionId: string, value: unknown) => void;
  questionLayout: 'stacked' | 'card';
  accentColor?: string;
  disabled?: boolean;
  /** Resolved questions after dynamic options expansion (if any) */
  resolvedQuestions?: QuestionDefinition[];
}

export const QuestionnaireStepView: React.FC<QuestionnaireStepViewProps> = ({
  step,
  answers,
  onAnswerChange,
  questionLayout,
  accentColor = '#2563eb',
  disabled = false,
  resolvedQuestions,
}) => {
  const questions = resolvedQuestions || step.questions;
  const isCard = questionLayout === 'card';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        animation: 'fadeIn 0.25s ease',
      }}
      data-testid={`questionnaire-step-${step.id}`}
    >
      {/* Step header */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h3 style={{
          margin: 0,
          fontSize: 18,
          fontWeight: 600,
          color: '#111827',
        }}>
          {step.title}
        </h3>
        {step.description && (
          <p style={{
            margin: 0,
            fontSize: 13,
            color: '#6b7280',
            lineHeight: 1.5,
          }}>
            {step.description}
          </p>
        )}
      </div>

      {/* Questions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: isCard ? 12 : 16 }}>
        {questions.map((question, idx) => {
          // Dynamic section header: show when a new _dynamicSection starts
          const dynamicSection = (question as unknown as Record<string, unknown>)['_dynamicSection'] as string | undefined;
          const prevSection = idx > 0
            ? (questions[idx - 1] as unknown as Record<string, unknown>)['_dynamicSection'] as string | undefined
            : undefined;
          const showSectionHeader = dynamicSection && dynamicSection !== prevSection;

          return (
            <React.Fragment key={question.id}>
              {showSectionHeader && (
                <>
                  {idx > 0 && (
                    <div style={{ height: 1, background: '#e5e7eb', margin: '4px 0' }} />
                  )}
                  <div style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: accentColor,
                    padding: '4px 0',
                    borderBottom: `2px solid ${accentColor}20`,
                  }}>
                    {dynamicSection}
                  </div>
                </>
              )}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  ...(isCard ? {
                    padding: 16,
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    background: '#fff',
                  } : {}),
                }}
              >
                <label style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#1f2937',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}>
                  {question.label}
                  {question.required && (
                    <span style={{ color: '#ef4444', fontSize: 12 }}>*</span>
                  )}
                </label>
                {question.description && (
                  <span style={{ fontSize: 12, color: '#9ca3af', marginBottom: 2 }}>
                    {question.description}
                  </span>
                )}
                <QuestionInput
                  question={question}
                  value={answers[question.id]}
                  onChange={onAnswerChange}
                  disabled={disabled}
                  accentColor={accentColor}
                />
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

QuestionnaireStepView.displayName = 'QuestionnaireStepView';
